import React, { useEffect, useState } from "react";
import type { ChangeEvent } from 'react';
import { connect, useDispatch } from "react-redux";
import styles from "../GlobalSearch.styles";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { selectSearch } from "../selectors";
import PersonIcon from "@mui/icons-material/Person";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SearchIcon from "@mui/icons-material/Search";
import StyledInputBase from "../../../common/components/styled-components/StyledInputBase";
import StyledSearchIconWrapper from "../../../common/components/styled-components/StyledSearchIconWrapper";
import StyledSearch from "../../../common/components/styled-components/StyledSearch";
import { findEntityByFiltersRequest } from "../actions/search";
import BlueSwitch from "../../../common/components/styled-components/Blueswitch";
import type { RootState } from '../../../reducers';
import ClearIcon from '@mui/icons-material/Clear';

const EntitySearch = (props: any) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState(null);
  const [limit, setLimit] = useState<number>(5);
  //TODO check and update

  // const [preparedToDisplayResult, setShowList] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filters, setFilters] = useState({ byKadId: false });
  const { localSearchEntityResult, handleChangeList, processing } = props;
  const [clearTooltipOpen, setClearTooltipOpen] = useState(false);

  useEffect(() => {
    let active = true;
    let newOptions: any = [];

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    if (active) {
      if (value) {
        newOptions = [value];
      }
      if (localSearchEntityResult) {
        newOptions = [...newOptions, ...localSearchEntityResult];
      }

      setOptions(newOptions);
    }

    return () => {
      active = false;
    };
  }, [dispatch, localSearchEntityResult, value, inputValue]);

  const increase = () => {
    setLimit((prev) => Math.min(prev + 5, 50));
  };

  const decrease = () => {
    setLimit((prev) => Math.max(prev - 5, 5));
  };

  const handleChange = (newInputValue: any) => {
    setInputValue(newInputValue);
    if (newInputValue.length >= 2) {
      const requestData = {
        ...filters,
        inputValue: newInputValue,
        limit: limit,
      };
      !value && dispatch(findEntityByFiltersRequest(requestData));
    }
  };

  const handleClear = () => {
    setClearTooltipOpen(false);
    setInputValue("");
    setValue(null);
    setOptions([]);
  };

  const handleChecked = (e: ChangeEvent<HTMLInputElement>) => {
    setFilters({...filters, byKadId: e.target.checked});
  }
  // console.log('filters', filters);
  // const getResultsList = () => {
  //   setInputValue("");
  //   setShowList(true);
  //   setOpen(false);
  // };

  return (
    <Box sx={{ marginBottom: "20px", marginTop: "5px" }}>
      <Box sx={styles.filters}>
        <FormControlLabel
          value="end"
          control={<BlueSwitch
            onChange={e => handleChecked(e)}
            checked={filters.byKadId}
          />}
          label="Search by kad ID"
          labelPlacement="end"
          //@ts-ignore
          // onChange={(e) => handleChecked("ip_restriction", e, -1)}
          // checked={user.ip_restriction}
          // defaultChecked={initialUserState.ip_restriction}
          style={{ color: "#495057" }}
        />
        <Box sx={styles.filters}>
          <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
            SHOW:
          </Typography>
          <ButtonGroup variant="text" aria-label="Basic button group">
            <Button
              sx={styles.splitButton}
              onClick={increase}
            >+{limit}</Button>
            <Button
              sx={styles.splitButton}
              onClick={decrease}
            >-5</Button>
          </ButtonGroup>
        </Box>
      </Box>
      <Autocomplete
        open={open && inputValue.length >= 2}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        options={options}
        loading={processing}
        filterOptions={(x) => x}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        blurOnSelect
        fullWidth
        getOptionLabel={(option) => {
          return option.name && option.age
            ? `${option.name} ${option.age}`
            : option.name && !option.age
            ? `${option.name}`
            : `${option.street}`;
        }}
        onChange={(_e, newValue) => {
          setOptions(newValue ? [newValue, ...options] : options);
          // setValue(newValue);
          handleChangeList(newValue);
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              <Grid container alignItems="center">
                <Grid sx={{ display: "flex" }}>
                  {option.name ? (
                    <PersonIcon sx={styles.resultIcon} />
                  ) : (
                    <ApartmentIcon sx={styles.resultIcon} />
                  )}
                  <Grid>
                    <Typography>
                      {option.name && option.age
                        ? `${option.name}, ${option.age} y.o.`
                        : option.name && !option.age
                        ? `${option.name}`
                        : `${option.street}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </li>
          );
        }}
        inputValue={inputValue}
        onInputChange={(_e, newInputValue) => handleChange(newInputValue)}
        renderInput={(params) => {
          const { InputLabelProps, InputProps, ...rest } = params
          return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <StyledSearch ref={params.InputProps.ref}>
              <StyledSearchIconWrapper>
                <SearchIcon sx={{ color: "#495057" }} />
              </StyledSearchIconWrapper>
              <StyledInputBase
                {...rest}
                // onKeyDown={(e) => getResultsList()}
                inputProps={{
                  ...params.inputProps,
                  placeholder: "Search",
                }}
                sx={{
                  "&.MuiInputBase-input": {paddingRight: "36px"},
                }}
              />
              <Tooltip
                title="Clear filed"
                open={clearTooltipOpen && inputValue.length > 0}
                onOpen={() => setClearTooltipOpen(true)}
                onClose={() => setClearTooltipOpen(false)}
              >
                <span>
                  <IconButton
                    onClick={handleClear}
                    sx={{
                      position: "absolute",
                      right: 0,
                      bottom: 2,
                      opacity: inputValue.length > 0 ? 1 : 0,
                      pointerEvents: inputValue.length > 0 ? "auto" : "none",
                    }}
                    size="small"
                  >
                    <ClearIcon
                      sx={{ color: "#495057" }}
                      fontSize="small"
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </StyledSearch>
          </Box>
        )}
      }
      />
    </Box>
  );
};

function mapStateToProps(state: RootState) {
  const { localSearchEntityResult, processing } = selectSearch(state);
  return { localSearchEntityResult, processing };
}

export default connect(mapStateToProps)(EntitySearch);
